import React, { useEffect, useState, useRef } from "react";
import Select, { components } from "react-select";
import logo_eagle from "./assets/img/logo_eagle.png";
import DOLLUZ_CORP from "./assets/img/DOLLUZ_CORP.png";
import { apiFetch, DASSIST_TICKET_FILES_BASE } from "./utils/api";
import { useNavigate } from "react-router-dom";
import { FaPaperclip, FaInfoCircle, FaFilePdf, FaFileExcel, FaSpinner, FaTimes, FaTrashAlt } from "react-icons/fa";
import { dnAlert } from "./utils/dNews_Alert";
import { exportTicketsExcel, exportTicketsPdf } from "./utils/ticketExport";
import "./dSpr.css";
import "./Support_Tickets.css";

function TicketDashboard({ navSize }) {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 7;

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [ticketStatus, setTicketStatus] = useState("");
    const [comments, setComments] = useState("");
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [originalStatus, setOriginalStatus] = useState("");
    const isStatusChanged = ticketStatus !== originalStatus;
    const [statusSaved, setStatusSaved] = useState(false);
    const [sendingMail, setSendingMail] = useState(false);
    const [history, setHistory] = useState([]);

    const [showEmpMenu, setShowEmpMenu] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const dropdownRef = useRef(null);
    const empDropdownRef = useRef(null);
    const modalRef = useRef(null);
    const [empId, setEmpId] = useState(null);
    const [expandedComments, setExpandedComments] = useState({});
    const [isClosed, setIsClosed] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);   // only Admin may delete tickets
    const [deleting, setDeleting] = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    useEffect(() => {
        fetchloginedEmployees();
        fetchTickets();
    }, [navigate]);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            const me = Array.isArray(data) ? data[0] : data;
            setIsAdmin(me?.emp_access_level === "Admin");

        } catch (err) {
            console.error("Error fetching employees:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchTickets = async () => {
        try {
            const res = await apiFetch(`/api/dassist_tickets/all`);
            const data = await res.json();
            setTickets(data);
        } catch (err) {
            console.error("Error fetching tickets:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async (ticketId) => {
        try {
            const res = await apiFetch(`/api/dassist_tickets/ticket_history/${ticketId}`);
            const data = await res.json();
            setHistory(data);
        } catch (err) {
            console.error("Error fetching history:", err);
        }
    };

    const toggleExpand = (index) => {
        setExpandedComments(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    useEffect(() => {
        if (selectedTicket) {
            setTicketStatus(selectedTicket.ticket_status);

            // ✅ Set isClosed based on ticket status
            setIsClosed(selectedTicket.ticket_status === "Closed");

            // Optional: reset comments and statusSaved
            setComments("");
            // Initialise the Send Mail button from the persisted flag (an unsent
            // status change / comment since the last mail) so its state is correct
            // across close-reopen AND page reloads - not just this modal session.
            setStatusSaved(!!selectedTicket.has_unsent_updates);
            setExpandedComments([]);

            // Fetch history
            fetchHistory(selectedTicket.ticket_id);
        }
        // Key on ticket_id, not the whole object: updating the OPEN ticket's
        // status after a save must NOT re-run this (it would wipe statusSaved and
        // disable Send Mail). Only opening a genuinely different ticket re-inits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTicket?.ticket_id]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            // Close Status Menu
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowStatusMenu(false);
            }

            // Close Employee Dropdown
            if (empDropdownRef.current && !empDropdownRef.current.contains(e.target)) {
                setShowEmpMenu(false);
            }

            // Close Modal
            if (isModalOpen && modalRef.current && !modalRef.current.contains(e.target)) {
                setIsModalOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isModalOpen]);

    // Filter selector options
    const filterOptions = [
        { value: "ticket_id", label: "Ticket ID" },
        { value: "emp_name", label: "Employee name" },
        { value: "emp_department", label: "Department" },
        { value: "category", label: "Category" },
        { value: "priority_level", label: "Priority" },
        { value: "contact_method", label: "Contact method" },
        { value: "subject", label: "Subject" },
        { value: "ticket_status", label: "Ticket status" },
        { value: "created_time", label: "Created date" },
    ];

    // State to track selected filter type
    const [selectedFilter, setSelectedFilter] = useState("ticket_id");

    // Define which columns to add filters for
    const filterableColumns = [
        "ticket_id",
        "emp_name",
        "emp_department",
        "category",
        "priority_level",
        "contact_method",
        "subject",
        "ticket_status",
        "created_time",
    ];

    // Dynamic filters state
    const [filters, setFilters] = useState(
        filterableColumns.reduce((acc, col) => ({ ...acc, [col]: [] }), {})
    );

    // Custom Option component with checkbox
    const CheckboxOption = (props) => {
        return (
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    style={{
                        marginRight: 8,
                        width: "14px",
                        height: "14px",
                        transform: "scale(1.2)",
                        cursor: "pointer"
                    }}
                />
                <label style={{ cursor: "pointer" }}>{props.label}</label>
            </components.Option>
        );
    };

    // Function to get options for each column dynamically
    const getOptions = (key) => {
        if (key === "created_time") {
            const uniqueDates = [...new Set(tickets.map(t => new Date(t.created_time).toISOString().split('T')[0]))];
            return uniqueDates.map(date => ({ value: date, label: date }));
        } else {
            const unique = [...new Set(tickets.map(t => t[key]))].filter(Boolean);
            return unique.map(u => ({ value: u, label: u }));
        }
    };

    // Filter tickets
    const filteredTickets = tickets.filter((t) =>
        filterableColumns.every((col) => {
            if (col === "created_time") {
                if (fromDate && !toDate) {
                    // only from-date filter
                    return new Date(t.created_time) >= new Date(fromDate);
                }

                if (!fromDate && toDate) {
                    // only to-date filter
                    return new Date(t.created_time) <= new Date(toDate);
                }

                if (fromDate && toDate) {
                    // full range filter
                    return (
                        new Date(t.created_time) >= new Date(fromDate) &&
                        new Date(t.created_time) <= new Date(toDate)
                    );
                }

                return true; // no date filter applied
            }

            // Old filter logic for all other fields
            if (!filters[col] || filters[col].length === 0) return true;

            return filters[col].some(f => f.value === t[col]);
        })
    );

    // ✅ Pagination
    const totalPages = Math.ceil(filteredTickets.length / rowsPerPage);
    const paginatedTickets = filteredTickets.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const saveTicketStatus = async () => {
        if (!selectedTicket) return;

        const statusChanged = ticketStatus !== selectedTicket.ticket_status;
        const commentAdded = comments.trim() !== "";

        const payload = {
            ticket_id: selectedTicket.ticket_id,
            ticket_status: statusChanged ? ticketStatus : null,
            ticket_comments: commentAdded ? comments : null,
            prev_status: selectedTicket.ticket_status,
        };

        try {
            const res = await apiFetch("/api/dassist_tickets/save_status", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Save failed");

            dnAlert.success("Success", data.message);

            setStatusSaved(true);
            fetchTickets();
            fetchHistory(selectedTicket.ticket_id);
            setComments("");

            // Reflect the just-saved status on the open ticket so status-gated UI
            // (e.g. the Admin Delete button) updates immediately, without needing
            // to close and reopen the modal.
            setSelectedTicket(prev => (prev ? { ...prev, ticket_status: ticketStatus } : prev));
            setOriginalStatus(ticketStatus);

            if (ticketStatus === "Closed") setIsClosed(true);
        } catch (err) {
            dnAlert.error("Error", err.message);
        }
    };

    // 🔹 Function to send ticket status email
    const sendTicketStatusMail = async (ticketId) => {
        if (!ticketId) return;
        setSendingMail(true); // start loader

        try {
            const res = await apiFetch("/api/dassist_tickets/send_status_mail", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ticket_id: ticketId }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to send mail");

            dnAlert.success("Success", "Email sent to employee successfully!");

            setStatusSaved(false);
            fetchTickets();   // refresh has_unsent_updates so a later reopen reflects the send
        } catch (err) {
            dnAlert.error("Error", err.message);
        } finally {
            setSendingMail(false);
        }
    };

    // 🔹 Delete ticket - Admin only, completed tickets only. Removes the ticket,
    // its history and its uploaded files (server-side).
    const deleteTicket = async (ticketId) => {
        if (!ticketId) return;
        const ok = await dnAlert.confirm({
            title: "Delete this ticket?",
            message: "This permanently removes the ticket, its history, and its attached files. This cannot be undone.",
            danger: true,
            confirmLabel: "Delete",
        });
        if (!ok) return;
        setDeleting(true);
        try {
            const res = await apiFetch("/api/dassist_tickets/delete_ticket", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ ticket_id: ticketId }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to delete ticket");
            dnAlert.success("Deleted", "Ticket removed successfully.");
            setShowModal(false);
            setSelectedTicket(null);
            fetchTickets();
        } catch (err) {
            dnAlert.error("Error", err.message);
        } finally {
            setDeleting(false);
        }
    };

    // ── Exports (themed Excel + PDF) ─────────────────────────────────
    // The actual brand/header/table chrome lives in utils/ticketExport.js
    // so all three ticket pages render an identical-looking report.
    const TICKET_COLUMNS = [
        { key: "ticket_id",     label: "Ticket ID",      width: 18, pdfWidth: 70 },
        { key: "emp_name",      label: "Employee",       width: 22, pdfWidth: 100 },
        { key: "emp_mail_id",   label: "Email",          width: 30, pdfWidth: 150 },
        { key: "emp_department",label: "Department",     width: 22 },
        { key: "category",      label: "Category",       width: 16 },
        { key: "priority_level",label: "Priority",       width: 12 },
        { key: "contact_method",label: "Contact method", width: 16 },
        { key: "subject",       label: "Subject",        width: 40, wrap: true },
        { key: "attachment",    label: "Attachment",     width: 14 },
        { key: "ticket_status", label: "Status",         width: 14 },
        { key: "created_time",  label: "Created date & time", width: 22 },
    ];
    const ticketsToRows = (tickets) => tickets.map(t => ({
        ticket_id:     t.ticket_id,
        emp_name:      t.emp_name,
        emp_mail_id:   t.emp_mail_id,
        emp_department:t.emp_department,
        category:      t.category,
        priority_level:t.priority_level,
        contact_method:t.contact_method,
        subject:       t.subject,
        attachment:    t.attachment_file ? (() => {
            try {
                const parsed = JSON.parse(t.attachment_file);
                return Array.isArray(parsed) ? `${parsed.length} file(s)` : "Yes";
            } catch { return "Yes"; }
        })() : "No",
        ticket_status: t.ticket_status,
        created_time:  new Date(t.created_time).toLocaleString(),
    }));
    const exportToPDF = (tickets) => {
        if (!tickets || tickets.length === 0) { dnAlert.warning("Nothing to export", "No tickets available to export."); return; }
        exportTicketsPdf({
            moduleName:  "dAssist",
            sheetTitle:  "Support Tickets",
            columns:     TICKET_COLUMNS,
            rows:        ticketsToRows(tickets),
            filename:    "Support_Tickets.pdf",
        });
    };
    const exportToExcel = async (tickets) => {
        if (!tickets || tickets.length === 0) { dnAlert.warning("Nothing to export", "No tickets available to export."); return; }
        await exportTicketsExcel({
            moduleName:  "dAssist",
            sheetTitle:  "Support Tickets",
            columns:     TICKET_COLUMNS,
            rows:        ticketsToRows(tickets),
            statusKey:   "ticket_status",
            filename:    "Support_Tickets.xlsx",
        });
    };

    const getStatusColor = (status) => {
        // Matches the status-dropdown colours (.status-* in the CSS) so a status
        // shows the same colour in the list, the dropdown, and the history.
        switch (status) {
            case "Open":            return "#b0bec5";
            case "In Review":       return "#f1c40f";
            case "In Progress":     return "#9b59b6";
            case "Action Pending":  return "#e67e22";
            case "Resolved":        return "#2ecc71";
            case "Reopen":          return "#007bff";
            case "Cancelled":       return "#e74c3c";
            case "Closed":          return "#1d6b26";
            default:                return "#333";
        }
    };

    if (loading) return <p>Loading tickets...</p>;

    return (
        <div className="ticket-dashboard-container">
            <div className={`ticket-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img
                                src={logo_eagle}
                                alt="dAdmin Logo"
                                style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }}
                            />
                        </a>
                        Support Tickets
                    </h2>
                    <p className="dspr-subtitle">
                        Every support request raised by employees. Filter, search, attach files, and reply
                        directly. Export the working list to PDF or Excel.
                    </p>
                </div>

                {/* Shared container */}
                <div className="ticket-dashboard-content">
                    {/* Filters */}
                    <div className="filters-container">
                        <div className="filters-left">
                            {/* Filter type selector */}
                            <Select
                                options={filterOptions}
                                value={filterOptions.find(f => f.value === selectedFilter)}
                                onChange={(val) => {
                                    setSelectedFilter(val.value);
                                    setFilters(prev => ({ ...prev, [val.value]: [] }));
                                    if (val.value === "created_time") {
                                        setFromDate("");
                                        setToDate("");
                                    }
                                    setCurrentPage(1);
                                }}
                                placeholder="Select filter"
                                isSearchable
                                styles={{ container: (provided) => ({ ...provided, width: 220 }) }}
                            />

                            {selectedFilter === "created_time" ? (
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <input
                                        type="date"
                                        className="date-input"
                                        value={fromDate}
                                        onClick={(e) => e.target.showPicker?.()}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />

                                    <span style={{ fontWeight: "bold" }}>to</span>

                                    <input
                                        type="date"
                                        className="date-input"
                                        value={toDate}
                                        onClick={(e) => e.target.showPicker?.()}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />

                                    {/* ❌ Reset Date Range Icon */}
                                    {(fromDate || toDate) && (
                                        <FaTimes
                                            size={18}
                                            color="#555555"
                                            title="Clear Date Filter"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                setFromDate("");
                                                setToDate("");
                                                setFilters(prev => ({ ...prev, created_time: [] }));
                                                setCurrentPage(1);
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <Select
                                    placeholder={`Filter by ${filterOptions.find(f => f.value === selectedFilter).label}`}
                                    options={getOptions(selectedFilter)}
                                    value={filters[selectedFilter]}
                                    onChange={(vals) => {
                                        setFilters(prev => ({ ...prev, [selectedFilter]: vals || [] }));
                                        setCurrentPage(1);
                                    }}
                                    closeMenuOnSelect={false}
                                    isMulti
                                    hideSelectedOptions={false}
                                    isClearable
                                    components={{ Option: CheckboxOption }}
                                    styles={{
                                        container: (provided) => ({ ...provided, width: 250 }),
                                        multiValue: (provided) => ({ ...provided, backgroundColor: "#e0e0e0" }),
                                    }}
                                />
                            )}

                            {/* Export icons */}
                            <FaFilePdf
                                size={24}
                                color="#e74c3c"
                                title="Export as PDF"
                                onClick={() => exportToPDF(filteredTickets)}
                            />
                            <FaFileExcel
                                size={24}
                                color="#27ae60"
                                title="Export as Excel"
                                onClick={() => exportToExcel(filteredTickets)}
                            />
                        </div>
                    </div>
                    <div className="table-top-wrapper">
                        <div className="table-wrapper">
                            <table className="ticket-table">
                                <thead>
                                    <tr>
                                        <th>Ticket ID</th>
                                        <th>Employee</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Category</th>
                                        <th>Priority</th>
                                        <th>Contact Method</th>
                                        <th>Subject</th>
                                        <th>Attachments</th>
                                        <th>Status</th>
                                        <th>Created Date & Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTickets.length > 0 ? (
                                        paginatedTickets.map((ticket) => (
                                            <tr key={ticket.ticket_id}>
                                                <td>
                                                    <a
                                                        href="#!"
                                                        style={{ color: "#4a90e2", cursor: "pointer", fontWeight: "500" }}
                                                        onClick={() => {
                                                            setSelectedTicket(ticket);
                                                            setTicketStatus(ticket.ticket_status);
                                                            setOriginalStatus(ticket.ticket_status);
                                                            setComments("");
                                                            fetchHistory(ticket.ticket_id);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        {ticket.ticket_id}
                                                    </a>
                                                </td>
                                                <td>{ticket.emp_name}</td>
                                                <td>{ticket.emp_mail_id}</td>
                                                <td>{ticket.emp_department}</td>
                                                <td>{ticket.category}</td>
                                                <td>{ticket.priority_level}</td>
                                                <td>{ticket.contact_method}</td>
                                                <td className="subject-col">{ticket.subject}</td>
                                                <td style={{ textAlign: "center" }}>
                                                    {ticket.attachment_file ? (() => {
                                                        let files = [];
                                                        try {
                                                            const parsed = JSON.parse(ticket.attachment_file);
                                                            files = Array.isArray(parsed) ? parsed : [ticket.attachment_file];
                                                        } catch {
                                                            files = [ticket.attachment_file];
                                                        }
                                                        return files.map((filePath, index) => {
                                                            const cleanPath = filePath.replace(/\\/g, "/");
                                                            const fileName = cleanPath.split("/").pop();
                                                            return (
                                                                <a
                                                                    key={index}
                                                                    href={`${DASSIST_TICKET_FILES_BASE}/${cleanPath}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ color: "#4a90e2", fontSize: "18px", marginRight: "8px" }}
                                                                    title={fileName}
                                                                >
                                                                    <FaPaperclip />
                                                                </a>
                                                            );
                                                        });
                                                    })() : (
                                                        <span style={{ color: "#999" }}>No File</span>
                                                    )}
                                                </td>
                                                <td
                                                    style={{
                                                        color: getStatusColor(ticket.ticket_status),
                                                        textAlign: 'center',
                                                        fontWeight: '500',
                                                        borderRadius: '6px',
                                                        padding: '4px 8px',
                                                    }}
                                                >
                                                    {ticket.ticket_status}
                                                </td>
                                                <td>{new Date(ticket.created_time).toLocaleString()}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="11" style={{ textAlign: "center" }}>
                                                No tickets found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "10px" }}>
                        <button className="pagination_btn" disabled={currentPage === 1} onClick={() => setCurrentPage((prev) => prev - 1)}>
                            Previous
                        </button>
                        <span style={{ marginTop: "8px" }}>
                            Page {currentPage} of {totalPages}
                        </span>
                        <button className="pagination_btn"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>

                {showModal && selectedTicket && (
                    <div className="ticket-modal-overlay" onClick={() => setShowStatusMenu(false)}>
                        <div
                            className="ticket-modal-content"
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: "relative" }}
                        >
                            {/* Close button - big hit area, react-icon glyph */}
                            <button
                                type="button"
                                className="close-icon"
                                onClick={() => setShowModal(false)}
                                title="Close"
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>

                            {/* Left panel */}
                            <div className="modal-left">
                                <h3 style={{ marginBottom: "20px" }}>Ticket details</h3>
                                <div className="ticket-detail-grid">
                                    <div className="detail-card"><span className="label">Employee</span><span className="value">{selectedTicket.emp_name}</span></div>
                                    <div className="detail-card"><span className="label">Email</span><span className="value">{selectedTicket.emp_mail_id}</span></div>
                                    <div className="detail-card"><span className="label">Department</span><span className="value">{selectedTicket.emp_department}</span></div>
                                    <div className="detail-card"><span className="label">Priority</span><span className="value priority">{selectedTicket.priority_level}</span></div>
                                    <div className="detail-card"><span className="label">Contact Method</span><span className="value">{selectedTicket.contact_method}</span></div>
                                    <div className="detail-card"><span className="label">Category</span><span className="value">{selectedTicket.category}</span></div>
                                    <div className="detail-card" style={{ gridColumn: "span 2" }}>
                                        <span className="label">Subject</span>
                                        <span className="value">{selectedTicket.subject}</span>
                                    </div>
                                    <div className="detail-card" style={{ gridColumn: "span 2" }}>
                                        <span className="label">Description</span>
                                        <span
                                            className="value description"
                                            dangerouslySetInnerHTML={{ __html: selectedTicket.description }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Right panel */}
                            <div className="modal-right">
                                {/* Status Row */}
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={{ fontWeight: "600", color: "#333" }}>Status:</span>
                                    <div className="status-dropdown-container" ref={dropdownRef} style={{ flex: 1 }}>
                                        <button
                                            className={`status-dropdown-button status-${ticketStatus.replace(/\s/g, '-')}`}
                                            onClick={() => !isClosed && setShowStatusMenu(prev => !prev)}
                                            disabled={isClosed}
                                        >
                                            {ticketStatus} <span className="arrow">{showStatusMenu ? "▲" : "▼"}</span>
                                        </button>

                                        {showStatusMenu && !isClosed && (
                                            <div className="status-dropdown-menu">
                                                {[
                                                    "In Review",
                                                    "In Progress",
                                                    "Action Pending",
                                                    "Resolved",
                                                    "Reopen",
                                                    "Cancelled",
                                                    "Closed"
                                                ]
                                                    .filter(status => status !== ticketStatus && !(ticketStatus !== "Open" && status === "Open"))
                                                    .map(status => (
                                                        <div
                                                            key={status}
                                                            className={`status-dropdown-item status-${status.replace(/\s/g, '-')}`}
                                                            onClick={() => {
                                                                setTicketStatus(status);
                                                                setShowStatusMenu(false);
                                                            }}
                                                        >
                                                            {status}
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Comments */}
                                <h4>Comments:</h4>
                                <textarea
                                    placeholder="Add comments..."
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                />

                                {/* Action Buttons */}
                                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                                    <button
                                        className="send-mail-btn"
                                        style={{ backgroundColor: "#e8520a" }}
                                        onClick={saveTicketStatus}
                                        disabled={!(isStatusChanged || comments.trim() !== "")}
                                    >
                                        Save
                                    </button>

                                    <button
                                        className="send-mail-btn"
                                        onClick={() => sendTicketStatusMail(selectedTicket?.ticket_id)}
                                        disabled={!statusSaved || sendingMail}
                                        style={{ display: "flex", alignItems: "center", gap: "6px" }}
                                    >
                                        {sendingMail ? (
                                            <>
                                                <FaSpinner className="spin" /> Sending...
                                            </>
                                        ) : (
                                            "Send Mail"
                                        )}
                                    </button>

                                    {statusSaved && (
                                        <div
                                            title={`This will send the latest status: "${ticketStatus}" and comments to the ticket creator.`}
                                            style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#555" }}
                                        >
                                            <FaInfoCircle />
                                        </div>
                                    )}

                                    {/* Delete - Admin only, and only once the ticket is terminal. */}
                                    {isAdmin && ["Resolved", "Closed", "Cancelled"].includes(selectedTicket?.ticket_status) && (
                                        <button
                                            className="send-mail-btn"
                                            style={{ backgroundColor: "#dc2626", marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}
                                            onClick={() => deleteTicket(selectedTicket?.ticket_id)}
                                            disabled={deleting}
                                            title="Permanently delete this completed ticket and its files"
                                        >
                                            {deleting ? <><FaSpinner className="spin" /> Deleting...</> : <><FaTrashAlt /> Delete Ticket</>}
                                        </button>
                                    )}
                                </div>

                                {/* History Section */}
                                <div className="history-section">
                                    <h4 style={{ marginBottom: "15px" }}>History: </h4>
                                    <div className="history-timeline">
                                        {history.length > 0 ? (
                                            history.map((h, index) => {
                                                const isExpanded = expandedComments[index];
                                                const isStatusOnly = h.ticket_status && !h.ticket_comments;
                                                const isCommentOnly = h.ticket_comments && !h.ticket_status;
                                                const hasBoth = h.ticket_status && h.ticket_comments; // In case future logic merges

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`history-item ${h.ticket_status
                                                            ? `status-${h.ticket_status.replace(/\s+/g, "-")}`
                                                            : 'status-comment'
                                                            }`}
                                                    >
                                                        {/* ✅ STATUS BLOCK (Show only if status exists) */}
                                                        {h.ticket_status && (
                                                            <div className="history-status">
                                                                <span className="label">Status:</span> <span style={{ color: getStatusColor(h.ticket_status), fontWeight: 700 }}>{h.ticket_status}</span>
                                                            </div>
                                                        )}

                                                        {/* ✅ STATUS META (Updated By + Time) */}
                                                        {h.ticket_status && (
                                                            <div className="history-meta">
                                                                <span className="label">Updated By:</span> {h.status_updated_by}
                                                                <span className="label">Time:</span>{" "}
                                                                {new Date(h.status_updated_time).toLocaleString()}
                                                            </div>
                                                        )}

                                                        {/* ✅ COMMENT BLOCK (Show only if comment exists) */}
                                                        {h.ticket_comments && (
                                                            <div className="history-meta" style={{ marginTop: "3px" }}>
                                                                <span className="label">Comments:</span>
                                                                {isExpanded ? h.ticket_comments : h.ticket_comments.slice(0, 50) + (h.ticket_comments.length > 50 ? "..." : "")}
                                                                {h.ticket_comments.length > 50 && (
                                                                    <span
                                                                        onClick={() => toggleExpand(index)}
                                                                        style={{ color: "#007bff", cursor: "pointer", marginLeft: "5px" }}
                                                                    >
                                                                        {isExpanded ? "See Less" : "See More"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* ✅ COMMENT META (Added By + Time) */}
                                                        {h.ticket_comments && (
                                                            <div className="history-meta">
                                                                <span className="label">Added By:</span> {h.comment_added_by}
                                                                <span className="label">Time:</span>{" "}
                                                                {new Date(h.comments_added_time).toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p>No history available.</p>
                                        )}
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default TicketDashboard;
